import React, { useCallback, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { getProvisioningStatus, getSaasErrorMessage, ProvisioningStatus } from './SaasApi';
import { PublicError, PublicLayout, PublicLoading } from './PublicLayout';
import { prepareProvisionedDashboardSession, reloadDashboard } from './DashboardNavigation';
import { existingProviderAgreementUrl } from './DashboardMigrationReturn';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

export const CheckoutReturnPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const checkoutIntentId = searchParams.get('checkoutIntentId') || '';
    const [authUser, setAuthUser] = useState<User | null>(auth.currentUser);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [status, setStatus] = useState<ProvisioningStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [retryNumber, setRetryNumber] = useState(0);
    const [dashboardError, setDashboardError] = useState<string | null>(null);
    const [ownerMismatch, setOwnerMismatch] = useState(false);
    const [dashboardRetryNumber, setDashboardRetryNumber] = useState(0);
    const attempts = useRef(0);

    // Firebase may still be restoring the owner session after Stripe redirects
    // back, so do not make authenticated calls from a transient null state.
    useEffect(() => onAuthStateChanged(auth, (currentUser) => {
        setAuthUser(currentUser);
        setIsAuthReady(true);
    }), []);

    const retry = useCallback(() => {
        attempts.current = 0;
        setError(null);
        setStatus(null);
        setOwnerMismatch(false);
        setRetryNumber((current) => current + 1);
    }, []);

    // A stale account from another tab cannot read this owner-scoped intent.
    // Sign it out explicitly instead of trapping the user in a retry loop.
    const signInAsClinicOwner = useCallback(async () => {
        try {
            await signOut(auth);
            navigate('/', { replace: true });
        } catch (signOutError: unknown) {
            setOwnerMismatch(false);
            setError(getSaasErrorMessage(
                signOutError,
                'Could not sign out the current account. Please sign out manually and try again.'
            ));
        }
    }, [navigate]);

    useEffect(() => {
        if (!checkoutIntentId || !isAuthReady || !authUser) return;
        let active = true;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const poll = async () => {
            attempts.current += 1;
            try {
                const nextStatus = await getProvisioningStatus(checkoutIntentId);
                if (!active) return;
                setStatus(nextStatus);
                if (nextStatus.status === 'manual_recovery') {
                    setError(nextStatus.errorMessage ||
                        'Stripe recorded this payment, but the automatic migration stopped for safety. ' +
                        `The clinic needs manual review. Contact support@superpenguin.com and quote ${checkoutIntentId}.`);
                    return;
                }
                if (nextStatus.status === 'expired') {
                    setError(nextStatus.errorMessage ||
                        (nextStatus.intentKind === 'existing_provider_migration' ?
                            'Stripe Checkout expired before payment. The existing agreement was not changed.' :
                            'Stripe Checkout expired before payment. Start checkout again.'));
                    return;
                }
                if (nextStatus.status === 'failed') {
                    setError(nextStatus.errorMessage ||
                        (nextStatus.intentKind === 'existing_provider_migration' ?
                            'The checkout could not be completed. The existing agreement was not changed.' :
                            'Your payment could not be provisioned. No access was granted.'));
                    return;
                }
                if (nextStatus.status !== 'provisioned' && attempts.current < MAX_POLL_ATTEMPTS) {
                    timer = setTimeout(poll, POLL_INTERVAL_MS);
                } else if (nextStatus.status !== 'provisioned') {
                    setError('Provisioning is taking longer than expected. Your payment status is safe; try checking again.');
                }
            } catch (pollError: unknown) {
                if (!active) return;
                const reason = (pollError as { details?: { reason?: string } })?.details?.reason;
                if (reason === 'checkout_owner_mismatch') {
                    setOwnerMismatch(true);
                    return;
                }
                setError(getSaasErrorMessage(pollError));
            }
        };

        void poll();
        return () => {
            active = false;
            if (timer) clearTimeout(timer);
        };
    }, [authUser, checkoutIntentId, isAuthReady, retryNumber]);

    // Once Stripe provisioning is authoritative, promote the owner session and
    // enter the dashboard without an intermediate checklist page.
    useEffect(() => {
        if (status?.status !== 'provisioned') return;
        let active = true;
        const openDashboard = async () => {
            setDashboardError(null);
            try {
                if (!authUser) {
                    throw new Error('Sign in with the clinic owner account to open your dashboard.');
                }
                if (!status.providerId) {
                    throw new Error('The new clinic is missing its dashboard reference. Please try again.');
                }
                if (status.intentKind === 'existing_provider_migration') {
                    // Root is a platform user rather than the clinic owner; reload through the provider-scoped
                    // Agreement deep link instead of resolving dashboard access against Root's home provider.
                    reloadDashboard(existingProviderAgreementUrl(status.providerId));
                    return;
                }
                await prepareProvisionedDashboardSession(authUser, status.providerId);
                reloadDashboard();
            } catch (dashboardFailure: unknown) {
                if (active) {
                    setDashboardError(getSaasErrorMessage(
                        dashboardFailure,
                        'Your clinic is ready, but we could not open its dashboard. Please try again.'
                    ));
                }
            }
        };
        void openDashboard();
        return () => { active = false; };
    }, [authUser, dashboardRetryNumber, status?.intentKind, status?.providerId, status?.status]);

    if (!checkoutIntentId) {
        return (
            <PublicLayout compact>
                <PublicError title='Checkout reference missing' message='Return to plans and start checkout again.' />
            </PublicLayout>
        );
    }
    if (!isAuthReady) {
        return <PublicLayout compact><PublicLoading message='Restoring your secure session…' /></PublicLayout>;
    }
    if (!authUser) {
        return (
            <PublicLayout compact>
                <PublicError
                    title='Sign in to finish opening your clinic'
                    message='Use the clinic owner account that completed checkout, then return to this page.'
                />
            </PublicLayout>
        );
    }
    if (ownerMismatch) {
        return (
            <PublicLayout compact>
                <PublicError
                    title='Sign in with the clinic owner'
                    message='Another dashboard account is active in this browser. Sign it out, then sign in with the email used at checkout.'
                    onRetry={() => { void signInAsClinicOwner(); }}
                    retryLabel='Sign out and continue'
                />
            </PublicLayout>
        );
    }
    if (error) {
        const isExistingProviderMigration = status?.intentKind === 'existing_provider_migration';
        const agreementReturnPath = status?.providerId ? existingProviderAgreementUrl(status.providerId) : '/';
        const title = status?.status === 'manual_recovery' ? 'This migration needs manual review' :
            status?.status === 'expired' ? 'Checkout expired' :
                status?.status === 'failed' ? 'Checkout was not completed' : 'We could not confirm your clinic';
        return (
            <PublicLayout compact>
                <PublicError
                    title={title}
                    message={error}
                    onRetry={status?.status === 'manual_recovery' || status?.status === 'expired' ||
                        (isExistingProviderMigration && status?.status === 'failed') ? undefined : retry}
                    returnPath={isExistingProviderMigration ? agreementReturnPath : '/plans'}
                    returnLabel={isExistingProviderMigration ? 'Return to organisation' : 'Return to plans'}
                    reloadReturn={isExistingProviderMigration}
                />
            </PublicLayout>
        );
    }
    if (!status || status.status !== 'provisioned') {
        const loadingMessage = status?.status === 'pending_payment' ?
            'Your payment is still processing…' :
            status?.status === 'payment_confirmed' ? 'Payment confirmed. Updating your clinic…' :
            status?.status === 'checkout_created' ? 'Confirming your subscription…' : 'Creating your clinic…';
        return (
            <PublicLayout compact>
                <PublicLoading message={loadingMessage} />
            </PublicLayout>
        );
    }

    if (dashboardError) {
        return (
            <PublicLayout compact>
                <PublicError
                    title='Your clinic is ready'
                    message={dashboardError}
                    onRetry={() => setDashboardRetryNumber((current) => current + 1)}
                />
            </PublicLayout>
        );
    }
    return <PublicLayout compact><PublicLoading message='Your clinic is ready. Opening your dashboard…' /></PublicLayout>;
};
